import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import * as JobDetailActions from './job-detail.actions';
import {
	Job,
	Division,
	OtherChargeItem,
	PagedResultsOfQuoteLogSummary,
	PagedResultsOfAuditTrail,
} from 'src/shared/api';
import { jobFormToJob } from '../job-detail.utility';
import { offeredServicesToAllowTbdJobDates } from 'src/shared/utilities/utility-functions';

export const jobDetailFeatureKey = 'job-detail';

export interface JobDetailsState extends EntityState<Job> {
	selectedJobId?: number;
	error?: any;
	isLoading?: boolean;
	editBillingInfo?: boolean;
	isSaving?: boolean;
	shouldStayOnPage?: boolean;
	railroadId?: number;
	customerId?: number;
	division?: Division;
	otherCharges?: OtherChargeItem[];
	isQuoteLogLoading?: boolean;
	isQuoteLogInitialized?: boolean;
	pagedQuoteLogResults?: PagedResultsOfQuoteLogSummary;
	allowTbdJobDates?: boolean;
	isJobLogLoading?: boolean;
	isJobLogInitialized?: boolean;
	pagedJobLogResults?: PagedResultsOfAuditTrail;
}

export const jobDetailAdapter = createEntityAdapter<Job>();

export interface JobDetailPartialState {
	readonly [jobDetailFeatureKey]: JobDetailsState;
}

export const initialState: JobDetailsState = jobDetailAdapter.getInitialState({
	isLoading: true,
	isSaving: false,
	editBillingInfo: false,
	shouldStayOnPage: true,
	otherCharges: [],
	pagedQuoteLogResults: { results: [], resultCount: 0 },
	allowTbdJobDates: false,
	pagedJobLogResults: { results: [], resultCount: 0 },
});

const jobReducer = createReducer(
	initialState,
	on(
		JobDetailActions.offeredServicesUpdated,
		(state, { offeredServices }) => ({
			...state,
			allowTbdJobDates: offeredServicesToAllowTbdJobDates(
				offeredServices
			),
		})
	),
	on(JobDetailActions.newJobStarted, (state) => ({
		...state,
		isLoading: false,
	})),

	on(JobDetailActions.jobLoadedFromStore, (state, { job }) => ({
		...state,
		railroadId: job.railroad ? job.railroad.id : undefined,
		customerId: job.customer ? job.customer.id : undefined,
		division: job.division,
		isLoading: false,
		allowTbdJobDates: offeredServicesToAllowTbdJobDates(
			job.offeredServices
		),
	})),
	on(
		JobDetailActions.editBillingInfoToggled,
		(state, { editBillingInfo }) => ({
			...state,
			editBillingInfo: editBillingInfo,
		})
	),
	on(JobDetailActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(JobDetailActions.newJobSaved, (state) =>
		jobDetailAdapter.removeOne(0, state)
	),
	on(JobDetailActions.newJobSaved, (state, { job }) =>
		jobDetailAdapter.addOne(job, { ...state, isSaving: false })
	),
	on(
		JobDetailActions.pagedQuoteLogsLoaded,
		(state, { pagedQuoteLogResults }) => ({
			...state,
			pagedQuoteLogResults,
			isQuoteLogLoading: false,
			isQuoteLogInitialized: true,
		})
	),
	on(JobDetailActions.otherChargeItemsLoaded, (state, { otherCharges }) => ({
		...state,
		otherCharges,
	})),
	on(
		JobDetailActions.shiftsAddedOutsideOfJobDates,
		JobDetailActions.ShiftsAddedFromAssignmentsOutsideOfJobDates,
		(state, { newDateRange }) =>
			jobDetailAdapter.updateOne(
				{
					id: state.selectedJobId,
					changes: {
						start: newDateRange.begin,
						end: newDateRange.end,
					},
				},
				{ ...state, isSaving: true }
			)
	),
	on(JobDetailActions.jobDatesExtended, (state) => ({
		...state,
		isSaving: false,
	})),
	on(JobDetailActions.jobSaved, (state, { job }) =>
		jobDetailAdapter.updateOne(
			{
				id: job.id,
				changes: {
					...job,
				},
			},
			{ ...state, isSaving: false }
		)
	),

	on(
		JobDetailActions.jobSelected,
		JobDetailActions.jobSelectedForAssignments,
		(state, { jobId }) => ({
			...state,
			selectedJobId: jobId,
			isQuoteLogLoading: true,
			isJobLogLoading: true
		})
	),

	on(JobDetailActions.jobLoadedFromApi, (state, { job }) =>
		jobDetailAdapter.upsertOne(job, {
			...state,
			railroadId: job.railroad ? job.railroad.id : undefined,
			customerId: job.customer ? job.customer.id : undefined,
			division: job.division,
			isLoading: false,
			allowTbdJobDates: offeredServicesToAllowTbdJobDates(
				job.offeredServices
			),
		})
	),
	on(JobDetailActions.documentsLoaded, (state, { documents }) =>
		jobDetailAdapter.updateOne(
			{
				id: state.selectedJobId,
				changes: {
					documents,
				},
			},
			state
		)
	),
	on(JobDetailActions.userConfirmedPrevailingWage, (state, { jobForm }) =>
		jobDetailAdapter.updateOne(
			{
				id: state.selectedJobId,
				changes: {
					...jobFormToJob(jobForm),
				},
			},
			state
		)
	),
	on(
		JobDetailActions.prevailingWagesConfirmed,
		(state, { prevailingWageInfo }) =>
			jobDetailAdapter.updateOne(
				{
					id: state.selectedJobId,
					changes: {
						prevailingWageInfo,
					},
				},
				state
			)
	),
	on(JobDetailActions.errorSavingJob, (state, { error }) => ({
		...state,
		error,
		isSaving: false,
	})),
	on(JobDetailActions.railroadSelected, (state, { railroadId }) => ({
		...state,
		railroadId,
		division: undefined,
	})),
	on(JobDetailActions.customerSelected, (state, { customerId }) => ({
		...state,
		customerId,
	})),
	on(JobDetailActions.divisionSelected, (state, { division }) => ({
		...state,
		division,
	})),
	on(JobDetailActions.jobDetailDestroyed, (state) => ({
		...state,
		isLoading: true,
		isSaving: false,
		editBillingInfo: false,
		isLoadingLogo: true,
		payrollType: undefined,
		error: undefined,
		contractedRates: undefined,
		shouldStayOnPage: true,
		railroadId: undefined,
		customerId: undefined,
		division: undefined,
		jobLogListMessage: undefined,
		jobLogs: [],
		isLoadingJobLogs: undefined,
		otherCharges: [],
		isQuoteLogLoading: undefined,
		isQuoteLogInitialized: undefined,
		pagedQuoteLogResults: { results: [], resultCount: 0 },
		allowTbdJobDates: false,
		isJobLogLoading: undefined,
		isJobLogInitialized: undefined,
		pagedJobLogResults: { results: [], resultCount: 0 },
	})),
	on(
		JobDetailActions.pagedJobLogsLoaded,
		(state, { pagedJobLogResults }) => ({
			...state,
			pagedJobLogResults,
			isJobLogLoading: false,
			isJobLogInitialized: true,
		})
	)
);

export function reducer(state: JobDetailsState | undefined, action: Action) {
	return jobReducer(state, action);
}
